/**
 * Calling and texting from Roof Board (ADR 0310).
 *
 * Jonathon, 2026-10-24: "i'd like to be able to call and text out of the
 * roofboard app." The Call button on a job does one of two things, and which
 * is decided here rather than on the screen so that the MCP server and the web
 * app cannot disagree (CLAUDE.md rule 5):
 *
 *   - **Through Roof Board.** The app rings the rep's own mobile from the Roof
 *     Board line, then connects the customer. The customer sees the company's
 *     number, the call is recorded and transcribed, and the words land on the
 *     job. Only for the lead sources marked for it, and only for a person
 *     whose profile says they place calls.
 *   - **From their own phone.** Everything else opens the phone's dialler with
 *     the number filled in — a `tel:` link — so nobody copies a number by hand
 *     again. Nothing is recorded or logged, which is how those calls were
 *     always made.
 *
 * The Phone screen's thread list is built here too: who has been in touch,
 * newest first, grouped by customer the way a phone groups by contact.
 */

import { toE164 } from "./partners.js";

/** A lead source as the Call button needs it. */
export interface CallingSource {
  name: string;
  calls_through_roof_board: boolean | null;
}

/** The signed-in person, as far as calling is concerned. */
export interface Caller {
  places_calls?: boolean | null;
  phone?: string | null;
}

export type CallPlan =
  /** Roof Board rings the rep, then dials `to`. */
  | { kind: "roof-board"; to: string }
  /** The phone's own dialler, with the number filled in. */
  | { kind: "own-phone"; to: string; href: string }
  /** No button: there is nobody to call. */
  | { kind: "none"; reason: string };

/**
 * Whether a job from this source is called through Roof Board.
 *
 * Decided 2026-10-24: Google, Thumbtack and Angi. The flag is on the lead
 * source row, so adding one is a tick in Tools rather than a release. A job
 * with no source, or a source nobody has listed, is called from the rep's own
 * phone.
 */
export function callsThroughRoofBoard(
  leadSource: string | null | undefined,
  sources: readonly CallingSource[],
): boolean {
  if (!leadSource) return false;
  const wanted = leadSource.trim().toLowerCase();
  return sources.some(
    (source) =>
      source.name.trim().toLowerCase() === wanted &&
      source.calls_through_roof_board === true,
  );
}

/**
 * What pressing Call does for this number on this job.
 *
 * The decision is the rep's standing first and the source's second: a person
 * not marked to place calls gets the dialler on every job, whatever the
 * source. A person who is marked but has no mobile on their profile gets the
 * dialler too, with nothing said — Roof Board cannot ring a phone it does not
 * know, and a button that fails is worse than one that dials.
 */
export function callPlan(input: {
  number: string | null | undefined;
  leadSource: string | null | undefined;
  sources: readonly CallingSource[];
  caller: Caller | null | undefined;
}): CallPlan {
  const to = toE164(input.number);
  if (!to) return { kind: "none", reason: "No phone number on file." };

  const mayPlace =
    input.caller?.places_calls === true && toE164(input.caller.phone) !== null;
  if (mayPlace && callsThroughRoofBoard(input.leadSource, input.sources)) {
    return { kind: "roof-board", to };
  }
  return { kind: "own-phone", to, href: `tel:${to}` };
}

/**
 * What a rep is told while Roof Board is placing the call.
 *
 * The phone rings within a couple of seconds of pressing the button; the
 * wording says what is about to happen so that an incoming call from the
 * company's own number is not a surprise.
 */
export const CALL_THROUGH_NOTICE =
  "Your phone will ring from the Roof Board line. Answer it and you will be connected.";

// ----------------------------------------------------------- the threads ---

/** One text, either way, from either line. */
export interface ThreadText {
  id: string;
  direction: "inbound" | "outbound";
  body: string | null;
  sent_at: string;
  contact_id: string | null;
  /** The customer's ten digits, for a text from a number with no contact. */
  phone: string | null;
  job_id?: string | null;
  /** How many pictures came with it. */
  media?: number;
}

/** One conversation on the Phone screen's Messages tab. */
export interface Thread {
  /** `contact:<id>` or `phone:<digits>` — the same shape the inbox uses. */
  key: string;
  contact_id: string | null;
  phone: string | null;
  /** The job the newest text was about, if any. */
  job_id: string | null;
  /** The newest text, which is the row's preview. */
  latest: ThreadText;
  /** Texts from them since we last answered — the unread dot. */
  unanswered: number;
  /** How many texts there are between us, both ways. */
  count: number;
}

/**
 * Group texts into conversations, newest first.
 *
 * By contact where one is known, by number where not, which is what a phone
 * does. "Unanswered" is their texts since our last one, the same rule the
 * inbox uses for "waiting on us" — but this list shows everyone, not only the
 * people waiting, because a Messages screen that hides half the conversations
 * is not a Messages screen.
 */
export function buildThreads(texts: readonly ThreadText[]): Thread[] {
  const byKey = new Map<string, ThreadText[]>();
  for (const text of texts) {
    const key = text.contact_id
      ? `contact:${text.contact_id}`
      : text.phone
        ? `phone:${text.phone}`
        : null;
    if (!key) continue;
    const list = byKey.get(key);
    if (list) list.push(text);
    else byKey.set(key, [text]);
  }

  const threads: Thread[] = [];
  for (const [key, all] of byKey) {
    const sorted = [...all].sort((a, b) => a.sent_at.localeCompare(b.sent_at));
    const latest = sorted.at(-1);
    if (!latest) continue;
    const lastReply = sorted
      .filter((t) => t.direction === "outbound")
      .reduce((late, t) => (t.sent_at > late ? t.sent_at : late), "");
    const unanswered = sorted.filter(
      (t) => t.direction === "inbound" && t.sent_at > lastReply,
    ).length;
    const aboutJob =
      [...sorted].reverse().find((t) => t.job_id)?.job_id ?? null;
    threads.push({
      key,
      contact_id: latest.contact_id,
      phone: latest.phone,
      job_id: aboutJob,
      latest,
      unanswered,
      count: sorted.length,
    });
  }

  threads.sort(
    (a, b) =>
      b.latest.sent_at.localeCompare(a.latest.sent_at) ||
      a.key.localeCompare(b.key),
  );
  return threads;
}

/** The one line under a name in the Messages list. */
export function threadPreview(text: ThreadText): string {
  const body = (text.body ?? "").replace(/\s+/g, " ").trim();
  const pictures =
    text.media && text.media > 0
      ? text.media === 1
        ? "1 picture"
        : `${text.media} pictures`
      : "";
  const said = body || pictures || "(no text)";
  return text.direction === "outbound" ? `You: ${said}` : said;
}

// ------------------------------------------------------------ the recents ---

/** A call as the Recents tab reads it. */
export interface RecentCall {
  direction: string;
  answered: boolean;
  status?: string | null;
  voicemail_at: string | null;
  duration_seconds: number | null;
  recording_seconds: number | null;
}

/**
 * The word beside a call in Recents, the way a phone's Recents says it.
 *
 * "Missed" is red on every phone and it is the one that needs calling back;
 * the others are history.
 */
export function recentsLabel(call: RecentCall): {
  label:
    "Outgoing" | "Incoming" | "Missed" | "Voicemail" | "No answer" | "Calling";
  missed: boolean;
} {
  if (call.voicemail_at) return { label: "Voicemail", missed: true };
  if (call.answered) {
    return call.direction === "inbound"
      ? { label: "Incoming", missed: false }
      : { label: "Outgoing", missed: false };
  }
  if (call.status === "ringing" || call.status === "in-progress")
    return { label: "Calling", missed: false };
  return call.direction === "inbound"
    ? { label: "Missed", missed: true }
    : { label: "No answer", missed: false };
}

// -------------------------------------------------------------- the badge ---

/**
 * The number on the phone bubble (ADR 0321).
 *
 * What a phone's badge means: things waiting on you. Here that is every
 * conversation with a customer's text we have not answered, plus every missed
 * call and voicemail since Recents was last looked at. A missed call stops
 * counting once the list has been seen, the way a phone clears its badge when
 * Recents is opened; an unanswered text keeps counting until somebody answers
 * it, because nothing but an answer makes it not waiting.
 */
export function phoneBadge(input: {
  threads: readonly Pick<Thread, "unanswered">[];
  calls: readonly (RecentCall & { started_at: string })[];
  /** When Recents or Voicemail was last on screen; null means never. */
  seenAt: string | null;
}): { waiting: number; missed: number; total: number } {
  const waiting = input.threads.filter((t) => t.unanswered > 0).length;
  const missed = input.calls.filter(
    (call) =>
      recentsLabel(call).missed &&
      (input.seenAt === null || call.started_at > input.seenAt),
  ).length;
  return { waiting, missed, total: waiting + missed };
}
